import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  IceCream, ChevronDown, Clock, MapPin, Phone,
  Instagram, Facebook, MessageCircle, Star, Heart, Award, Leaf
} from 'lucide-react';
import { useCategories } from '../../hooks/useCategories';
import { useProducts } from '../../hooks/useProducts';
import { safeArray } from '../../utils/safe';
import './Landing.css';
import heroImage from './assets/hero_gelato_background_1765329114627.png';
import menuBg from './assets/menu_section_background_1765329140576.png';
import { getImageUrl } from '../../utils/url';

const ALL_CATEGORY = 'Todos';

function formatCOP(price: number): string {
  return '$ ' + Math.round(price).toLocaleString('es-CO');
}

export default function Landing() {
  const [active, setActive] = useState<string>(ALL_CATEGORY);
  const [scrolled, setScrolled] = useState(false);

  const { data: categories = [], isLoading: loadingCategories } = useCategories();
  const { data: products = [], isLoading: loadingProducts } = useProducts();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll-reveal via IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('visible');
      }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loadingProducts]);

  const safeProducts = safeArray(products);
  const filtered = active === ALL_CATEGORY
    ? safeProducts
    : safeProducts.filter(p =>
        typeof p.category === 'string' ? p.category === active : p.category?.name === active
      );

  const scrollToMenu = () =>
    document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="landing-root">

      {/* ── Navigation ─────────────────────────────────── */}
      <nav className={`elegant-nav ${scrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="nav-logo">
          <IceCream size={20} className="nav-logo-icon" />
          DUFI GELATO
        </Link>
        <div className="nav-menu">
          <a href="#about-section" className="nav-menu-link">Nosotros</a>
          <a href="#menu-section" className="nav-menu-link">Menú</a>
          <a href="#location-section" className="nav-menu-link">Ubicación</a>
          <Link to="/login" className="nav-cta-btn">Sistema</Link>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────── */}
      <section className="hero-section" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="hero-badge">
            <IceCream size={16} /> &nbsp;Heladería &amp; Comidas Rápidas
          </div>
          <h1 className="hero-title">DUFI GELATO</h1>
          <p className="hero-subtitle">Sabores que enamoran</p>
          <p className="hero-description">
            Gelatos artesanales y comidas rápidas preparadas con ingredientes
            seleccionados. Una experiencia única en cada bocado.
          </p>
          <div className="hero-actions">
            <a
              href="#menu-section"
              className="hero-btn-primary"
              onClick={e => { e.preventDefault(); scrollToMenu(); }}
            >
              Ver Menú
            </a>
            <a href="#location-section" className="hero-btn-secondary">
              Encuéntranos
            </a>
          </div>
        </div>
        <button className="scroll-indicator" onClick={scrollToMenu} aria-label="Ir al menú">
          <ChevronDown size={30} color="#d4a574" />
        </button>
      </section>

      {/* ── Features strip ──────────────────────────────── */}
      <section className="features-strip">
        <div className="features-inner">
          <div className="feature-item">
            <Star size={22} className="feature-icon" />
            <span>Sabores Artesanales</span>
          </div>
          <span className="feature-sep" />
          <div className="feature-item">
            <Leaf size={22} className="feature-icon" />
            <span>Ingredientes Naturales</span>
          </div>
          <span className="feature-sep" />
          <div className="feature-item">
            <Heart size={22} className="feature-icon" />
            <span>Hecho con Amor</span>
          </div>
          <span className="feature-sep" />
          <div className="feature-item">
            <Award size={22} className="feature-icon" />
            <span>Calidad Premium</span>
          </div>
        </div>
      </section>

      {/* ── About ───────────────────────────────────────── */}
      <section id="about-section" className="about-section">
        <div className="about-container reveal">
          <div className="about-text">
            <span className="eyebrow">Nuestra Historia</span>
            <h2 className="about-title">Pasión por el sabor<br />desde el primer día</h2>
            <p className="about-body">
              En Dufi Gelato creemos que un buen helado puede transformar cualquier
              momento. Elaboramos nuestros gelatos de forma artesanal con ingredientes
              seleccionados, combinando sabores tradicionales con creaciones únicas que
              sorprenden a cada cliente.
            </p>
            <p className="about-body">
              Además de nuestras delicias heladas, ofrecemos comidas rápidas preparadas
              con el mismo amor y dedicación para que cada visita sea una experiencia
              completa e inolvidable.
            </p>
            <a
              href="#menu-section"
              className="about-link"
              onClick={e => { e.preventDefault(); scrollToMenu(); }}
            >
              Descubre Nuestro Menú →
            </a>
          </div>
          <div className="about-visual">
            <div className="about-img-wrap">
              <img src={heroImage} alt="Dufi Gelato" className="about-img" />
              <div className="about-badge">
                <IceCream size={28} color="#d4a574" />
                <span>Artesanal<br />& Fresco</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Menu banner ─────────────────────────────────── */}
      <section
        id="menu-section"
        className="menu-banner"
        style={{ backgroundImage: `url(${menuBg})` }}
      >
        <div className="menu-banner-overlay" />
        <div className="menu-banner-content">
          <span className="eyebrow light">Nuestras Delicias</span>
          <h2 className="menu-banner-title">Nuestro Menú</h2>
        </div>
      </section>

      {/* ── Category filters ────────────────────────────── */}
      <section className="categories-section">
        <div className="categories-inner">
          {loadingCategories ? (
            [1,2,3,4].map(i => (
              <div key={i} className="skeleton cat-skeleton" />
            ))
          ) : (
            [ALL_CATEGORY, ...categories.map(c => c.name)].map(c => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`category-chip ${active === c ? 'active' : ''}`}
              >
                {c}
              </button>
            ))
          )}
        </div>
      </section>

      {/* ── Products ────────────────────────────────────── */}
      <section className="products-section">
        {loadingProducts ? (
          <div className="products-grid">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="product-card skeleton-card">
                <div className="skeleton pcard-img-skel" />
                <div className="pcard-body">
                  <div className="skeleton" style={{ height: 12, width: '50%', borderRadius: 6, marginBottom: 10 }} />
                  <div className="skeleton" style={{ height: 20, width: '80%', borderRadius: 6, marginBottom: 8 }} />
                  <div className="skeleton" style={{ height: 14, width: '90%', borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="products-grid">
            {filtered.map((product, idx) => {
              const imageUrl = getImageUrl(product.imageUrl as any);
              const catName = typeof product.category === 'string'
                ? product.category
                : product.category?.name;
              return (
                <div
                  key={product.id}
                  className="product-card reveal"
                  style={{ transitionDelay: `${(idx % 6) * 60}ms` }}
                >
                  <div className="pcard-img-wrap">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={product.name}
                        className="pcard-img"
                        onError={e => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement?.classList.add('pcard-no-img');
                        }}
                      />
                    ) : (
                      <div className="pcard-img-placeholder">
                        <IceCream size={56} color="rgba(212,165,116,0.45)" />
                      </div>
                    )}
                  </div>
                  <div className="pcard-body">
                    {catName && <span className="pcard-cat">{catName}</span>}
                    <h3 className="pcard-name">{product.name}</h3>
                    {product.description && (
                      <p className="pcard-desc">{product.description}</p>
                    )}
                    <div className="pcard-footer">
                      <span className="pcard-price">{formatCOP(product.price)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            <IceCream size={64} className="empty-icon" />
            <h3 className="empty-title">Sin productos en esta categoría</h3>
            <p className="empty-text">Selecciona otra categoría para ver más opciones</p>
          </div>
        )}
      </section>

      {/* ── Gallery ─────────────────────────────────────── */}
      <section id="gallery-section" className="gallery-section">
        <div className="gallery-header reveal">
          <span className="eyebrow">Galería</span>
          <h2 className="gallery-title">Momentos Dufi</h2>
          <p className="gallery-subtitle">Cada visita es una experiencia única</p>
        </div>
        <div className="gallery-grid reveal">
          <div className="g-item g-tall" style={{ backgroundImage: `url(${heroImage})` }} />
          <div className="g-item" style={{ backgroundImage: `url(${menuBg})` }} />
          <div className="g-item" style={{ backgroundImage: `url(${heroImage})` }} />
          <div className="g-item g-wide" style={{ backgroundImage: `url(${menuBg})` }} />
          <div className="g-item" style={{ backgroundImage: `url(${heroImage})` }} />
        </div>
        <p className="gallery-note">📸 Próximamente más fotos de nuestros productos y local</p>
      </section>

      {/* ── Location & Hours ───────────────────────────── */}
      <section id="location-section" className="location-section">
        <div className="location-container reveal">
          <div className="location-info">
            <span className="eyebrow">Visítanos</span>
            <h2 className="location-title">Encuéntranos</h2>
            <div className="info-list">
              <div className="info-row">
                <div className="info-icon-wrap">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="info-label">Dirección</p>
                  <p className="info-value">Tu dirección aquí, Ciudad — Colombia</p>
                </div>
              </div>
              <div className="info-row">
                <div className="info-icon-wrap">
                  <Clock size={20} />
                </div>
                <div>
                  <p className="info-label">Horarios de Atención</p>
                  <p className="info-value">Lunes a Viernes: 10:00 am – 9:00 pm</p>
                  <p className="info-value">Sábados y Domingos: 10:00 am – 10:00 pm</p>
                </div>
              </div>
              <div className="info-row">
                <div className="info-icon-wrap">
                  <Phone size={20} />
                </div>
                <div>
                  <p className="info-label">Teléfono / WhatsApp</p>
                  <p className="info-value">+57 (XXX) XXX-XXXX</p>
                </div>
              </div>
            </div>
          </div>
          <div className="location-map">
            <div className="map-placeholder">
              <MapPin size={44} color="#d4a574" />
              <p className="map-placeholder-title">Mapa próximamente</p>
              <span className="map-placeholder-sub">Agrega tu dirección exacta para mostrar el mapa</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── WhatsApp FAB ───────────────────────────────── */}
      <a
        href="https://wa.me/57XXXXXXXXXX"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-fab"
        aria-label="Contáctanos por WhatsApp"
      >
        <MessageCircle size={28} />
        <span className="fab-tooltip">¡Escríbenos!</span>
      </a>

      {/* ── Footer ─────────────────────────────────────── */}
      <footer className="elegant-footer">
        <div className="footer-top">
          <div className="footer-brand">
            <IceCream size={36} color="#d4a574" />
            <h3 className="footer-logo">DUFI GELATO</h3>
            <p className="footer-tagline">Heladería &amp; Comidas Rápidas</p>
          </div>
          <div className="footer-col">
            <h4 className="footer-col-title">Navegación</h4>
            <a href="#about-section" className="footer-link">Nosotros</a>
            <a href="#menu-section" className="footer-link">Menú</a>
            <a href="#gallery-section" className="footer-link">Galería</a>
            <a href="#location-section" className="footer-link">Ubicación</a>
          </div>
          <div className="footer-col">
            <h4 className="footer-col-title">Síguenos</h4>
            <div className="social-row">
              <a href="#" className="social-btn" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="#" className="social-btn" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a
                href="https://wa.me/57XXXXXXXXXX"
                className="social-btn"
                aria-label="WhatsApp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle size={20} />
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2025 DUFI Gelato · Todos los derechos reservados · Hecho con ❤️ para nuestros clientes</p>
        </div>
      </footer>

    </div>
  );
}
